import {
  Environment,
  Lightformer,
  Plane,
  RenderTexture,
  useGLTF,
  useTexture,
} from "@react-three/drei";
import { Canvas, extend, useFrame, useThree } from "@react-three/fiber";
import {
  BallCollider,
  CuboidCollider,
  Physics,
  RigidBody,
  useRopeJoint,
  useSphericalJoint,
} from "@react-three/rapier";
import { MeshLineGeometry, MeshLineMaterial } from "meshline";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { BadgeTexture } from "./components/BadgeTexture";

extend({ MeshLineGeometry, MeshLineMaterial });
useGLTF.preload("images/black-chain.glb");

export default function App() {
  // const { debug } = useControls({ debug: false });
  const [user, setUser] = useState({
    firstName: "Ng",
    lastName: "Jia Wei",
    title: "Co-Founder & Partner",
    qrLink: "https://jiaweing.com",
  });
  const [bandTexture, setBandTexture] = useState(null);
  const [badgeTexture, setBadgeTexture] = useState(null);

  const handleImageUpload = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const texture = new THREE.Texture(img);
          texture.needsUpdate = true;
          if (type === "band") {
            setBandTexture(texture);
          } else {
            setBadgeTexture(texture);
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      {/* <div
        style={{
          position: "absolute",
          zIndex: 1,
          left: 10,
          top: 10,
          color: "white",
        }}
      >
        <input
          type="text"
          value={user.firstName}
          onChange={(e) =>
            setUser((prev) => ({ ...prev, firstName: e.target.value }))
          }
          placeholder="First Name"
          style={{ display: "block", margin: "5px", padding: "5px" }}
        />
        <input
          type="text"
          value={user.lastName}
          onChange={(e) =>
            setUser((prev) => ({ ...prev, lastName: e.target.value }))
          }
          placeholder="Last Name"
          style={{ display: "block", margin: "5px", padding: "5px" }}
        />
        <input
          type="text"
          value={user.title}
          onChange={(e) =>
            setUser((prev) => ({ ...prev, title: e.target.value }))
          }
          placeholder="Title"
          style={{ display: "block", margin: "5px", padding: "5px" }}
        />
        <input
          type="text"
          value={user.qrLink}
          onChange={(e) =>
            setUser((prev) => ({ ...prev, qrLink: e.target.value }))
          }
          placeholder="QR Code Link"
          style={{ display: "block", margin: "5px", padding: "5px" }}
        />
        <div style={{ margin: "10px 5px" }}>
          <label style={{ display: "block", marginBottom: "5px" }}>
            Band Texture:
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, "band")}
            style={{
              display: "block",
              marginBottom: "10px",
              color: "white",
            }}
          />
        </div>
        <div style={{ margin: "10px 5px" }}>
          <label style={{ display: "block", marginBottom: "5px" }}>
            Badge Texture:
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, "badge")}
            style={{
              display: "block",
              color: "white",
            }}
          />
        </div>
      </div> */}
      <Canvas shadows camera={{ position: [0, 0, 13], fov: 25 }}>
        <ambientLight intensity={Math.PI / 2} />
        <directionalLight
          position={[3, 3, 3]}
          intensity={Math.PI}
          castShadow
          shadow-mapSize={[2048, 2048]}
        />
        <Physics
          // debug={debug}
          interpolate
          gravity={[0, -40, 0]}
          timeStep={1 / 60}
        >
          <Band
            user={user}
            customTexture={bandTexture}
            customBadgeTexture={badgeTexture}
          />
        </Physics>
        <Plane
          receiveShadow
          args={[100, 100]}
          position={[0, 0, -0.2]}
          rotation={[0, 0, 0]}
        >
          <shadowMaterial transparent opacity={0.1} />
        </Plane>
        <Environment background blur={0}>
          <color attach="background" args={["#e3e3e3"]} />
          <Lightformer
            intensity={2}
            color="white"
            position={[0, -1, 5]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={3}
            color="white"
            position={[-1, -1, 1]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={3}
            color="white"
            position={[1, 1, 1]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={10}
            color="white"
            position={[-10, 0, 14]}
            rotation={[0, Math.PI / 2, Math.PI / 3]}
            scale={[100, 10, 1]}
          />
        </Environment>
      </Canvas>
    </>
  );
}

function Band({
  maxSpeed = 50,
  minSpeed = 10,
  user,
  customTexture,
  customBadgeTexture,
}) {
  const band = useRef(), fixed = useRef(), j1 = useRef(), j2 = useRef(), j3 = useRef(), card = useRef(), bandShadow = useRef() // prettier-ignore
  const vec = new THREE.Vector3(), ang = new THREE.Vector3(), rot = new THREE.Vector3(), dir = new THREE.Vector3() // prettier-ignore
  const segmentProps = {
    type: "dynamic",
    canSleep: true,
    colliders: false,
    angularDamping: 2,
    linearDamping: 2,
  };
  const { nodes, materials } = useGLTF("images/black-chain.glb");
  const defaultTexture = useTexture("images/base7-lanyard.png");
  const { width, height } = useThree((state) => state.size);
  const [curve] = useState(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(),
        new THREE.Vector3(),
        new THREE.Vector3(),
        new THREE.Vector3(),
      ])
  );
  const [dragged, drag] = useState(false);
  const [hovered, hover] = useState(false);

  const texture = customTexture || defaultTexture;

  useRopeJoint(fixed, j1, [[0, 0, 0], [0, 0, 0], 1]) // prettier-ignore
  useRopeJoint(j1, j2, [[0, 0, 0], [0, 0, 0], 1]) // prettier-ignore
  useRopeJoint(j2, j3, [[0, 0, 0], [0, 0, 0], 1]) // prettier-ignore
  useSphericalJoint(j3, card, [[0, 0, 0], [0, 1.45, 0]]) // prettier-ignore

  useEffect(() => {
    if (hovered) {
      document.body.style.cursor = dragged ? "grabbing" : "grab";
      return () => void (document.body.style.cursor = "auto");
    }
  }, [hovered, dragged]);

  useFrame((state, delta) => {
    if (dragged) {
      vec.set(state.pointer.x, state.pointer.y, 0.5).unproject(state.camera);
      dir.copy(vec).sub(state.camera.position).normalize();
      vec.add(dir.multiplyScalar(state.camera.position.length()));
      [card, j1, j2, j3, fixed].forEach((ref) => ref.current?.wakeUp());
      card.current?.setNextKinematicTranslation({
        x: vec.x - dragged.x,
        y: vec.y - dragged.y,
        z: vec.z - dragged.z,
      });
    }
    if (fixed.current) {
      // Fix most of the jitter when over pulling the card
      [j1, j2].forEach((ref) => {
        if (!ref.current.lerped)
          ref.current.lerped = new THREE.Vector3().copy(
            ref.current.translation()
          );
        const clampedDistance = Math.max(
          0.1,
          Math.min(1, ref.current.lerped.distanceTo(ref.current.translation()))
        );
        ref.current.lerped.lerp(
          ref.current.translation(),
          delta * (minSpeed + clampedDistance * (maxSpeed - minSpeed))
        );
      });
      // Calculate catmul curve
      curve.points[0].copy(j3.current.translation());
      curve.points[1].copy(j2.current.lerped);
      curve.points[2].copy(j1.current.lerped);
      curve.points[3].copy(fixed.current.translation());
      const points = curve.getPoints(32);
      band.current.geometry.setPoints(points);

      // Update shadow tube
      if (bandShadow.current) {
        const tubeGeometry = new THREE.TubeGeometry(curve, 32, 0.05, 8, false);
        bandShadow.current.geometry.dispose();
        bandShadow.current.geometry = tubeGeometry;
      }

      // Tilt it back towards the screen
      ang.copy(card.current.angvel());
      rot.copy(card.current.rotation());
      card.current.setAngvel({ x: ang.x, y: ang.y - rot.y * 0.25, z: ang.z });
    }
  });

  curve.curveType = "chordal";
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;

  return (
    <>
      <group position={[0, 4, 0]}>
        <RigidBody ref={fixed} {...segmentProps} type="fixed" />
        <RigidBody position={[0.5, 0, 0]} ref={j1} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[1, 0, 0]} ref={j2} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[1.5, 0, 0]} ref={j3} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody
          position={[2, 0, 0]}
          ref={card}
          {...segmentProps}
          type={dragged ? "kinematicPosition" : "dynamic"}
        >
          <CuboidCollider args={[0.8, 1.125, 0.01]} />
          <group
            scale={2.25}
            position={[0, -1.2, -0.05]}
            onPointerOver={() => hover(true)}
            onPointerOut={() => hover(false)}
            onPointerUp={(e) => (
              e.target.releasePointerCapture(e.pointerId), drag(false)
            )}
            onPointerDown={(e) => (
              e.target.setPointerCapture(e.pointerId),
              drag(
                new THREE.Vector3()
                  .copy(e.point)
                  .sub(vec.copy(card.current.translation()))
              )
            )}
          >
            <mesh geometry={nodes.card.geometry} castShadow>
              <meshPhysicalMaterial
                clearcoat={1}
                clearcoatRoughness={0.15}
                iridescence={1}
                iridescenceIOR={1}
                iridescenceThicknessRange={[0, 2400]}
                metalness={0.5}
                roughness={0.3}
              >
                <RenderTexture attach="map" width={2000} height={2000}>
                  <BadgeTexture
                    user={user}
                    customTexture={customBadgeTexture}
                  />
                </RenderTexture>
              </meshPhysicalMaterial>
            </mesh>
            <mesh
              geometry={nodes.clip.geometry}
              material={materials.metal}
              material-roughness={0.3}
              castShadow
            />
            <mesh
              geometry={nodes.clamp.geometry}
              material={materials.metal}
              castShadow
            />
          </group>
        </RigidBody>
      </group>
      <mesh ref={band}>
        <meshLineGeometry />
        <meshLineMaterial
          color="white"
          depthTest={false}
          resolution={[width, height]}
          useMap
          map={texture}
          repeat={[-3, 1]}
          lineWidth={1}
        />
      </mesh>
      <mesh ref={bandShadow} castShadow>
        <tubeGeometry args={[curve, 32, 0.05, 8, false]} />
        <meshBasicMaterial color="black" opacity={0.5} transparent />
      </mesh>
    </>
  );
}
